import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './authContext';

const WalkthroughContext = createContext();

export const WalkthroughProvider = ({ children }) => {
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [walkthroughStarted, setWalkthroughStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, userProfile, refreshUserProfile } = useAuth();
  
  // Track API calls to prevent duplicates
  const apiCallInProgress = useRef(false);
  const hasTrackedCompletion = useRef(false);
  const justCompleted = useRef(false);
  const completionTimestamp = useRef(null);

  // Check if screen is mobile width
  useEffect(() => {
    const checkMobile = () => {
      const isMobileWidth = window.innerWidth < 768; // Tailwind's md breakpoint
      setIsMobile(isMobileWidth);
    };

    // Check on mount
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simplified new user detection
  useEffect(() => {
    if (user && !userProfile) {
      // Check if this is a new session by looking at localStorage
      const isNewSession = !localStorage.getItem('hasSeenWalkthrough') &&
                          !localStorage.getItem('userSessionStarted');

      if (isNewSession) {
        localStorage.setItem('userSessionStarted', 'true');
        setIsNewUser(true);
      }
    }
  }, [user, userProfile]);

  // Walkthrough trigger logic - checks database status
  useEffect(() => {
    // Don't trigger if walkthrough is already showing or started
    if (showWalkthrough || walkthroughStarted) {
      return;
    }
    
    // Don't trigger if we just completed it (within last 5 seconds)
    if (justCompleted.current) {
      const timeSinceCompletion = completionTimestamp.current 
        ? Date.now() - completionTimestamp.current 
        : Infinity;
      if (timeSinceCompletion < 5000) {
        return;
      } else {
        // Reset flag after cooldown period
        justCompleted.current = false;
        completionTimestamp.current = null;
      }
    }
    
    if (user && userProfile) {
      // Check database walkthrough status first (most reliable)
      const walkthroughStatus = userProfile.walkthrough_status || 'not_started';
      const hasCompletedInDB = walkthroughStatus === 'completed';
      const isInProgress = walkthroughStatus === 'in_progress';
      
      // If already completed, don't show
      if (hasCompletedInDB) {
        localStorage.setItem('hasSeenWalkthrough', 'true');
        justCompleted.current = false;
        return;
      }
      
      // If in progress, don't auto-trigger (user might have closed it)
      if (isInProgress) {
        return;
      }
      
      // If walkthrough status is 'not_started' in DB, clear localStorage to allow it to show
      if (walkthroughStatus === 'not_started') {
        localStorage.removeItem('hasSeenWalkthrough');
      }
      
      // Also check localStorage as fallback
      const hasSeenWalkthrough = localStorage.getItem('hasSeenWalkthrough');
      
      // If already seen in localStorage and status is not 'not_started', don't show
      if (hasSeenWalkthrough && walkthroughStatus !== 'not_started') {
        return;
      }
      
      // Determine if user is new: created within last 10 minutes OR walkthrough status is 'not_started'
      const createdAtField = userProfile.created_at || userProfile.createdAt || userProfile.created;
      let isNew = false;
      
      if (createdAtField) {
        const userCreatedAt = new Date(createdAtField);
        const now = new Date();
        const timeDiff = now - userCreatedAt;
        isNew = timeDiff < 10 * 60 * 1000; // 10 minutes
      } else {
        isNew = isNewUser;
      }

      // Trigger walkthrough if:
      // 1. User is new (created < 10 min ago) OR walkthrough status is 'not_started'
      // 2. Walkthrough hasn't been completed in database
      // 3. Not on mobile
      // 4. Not already seen in localStorage (unless status is 'not_started' in DB)
      const shouldShow = !hasCompletedInDB && 
                       !isInProgress &&
                       (isNew || walkthroughStatus === 'not_started') && 
                       (!hasSeenWalkthrough || walkthroughStatus === 'not_started') &&
                       !isMobile;

      if (shouldShow) {
        setIsNewUser(true);
        setWalkthroughStarted(true);
        
        // If we're on the Explore page, wait for animation to finish
        if (window.location.pathname === '/explore') {
          setTimeout(() => {
            setShowWalkthrough(true);
          }, 6000);
        } else {
          setShowWalkthrough(true);
        }
      }
    }
  }, [user, userProfile, showWalkthrough, walkthroughStarted, isMobile, isNewUser]);

  // Efficient API call helper
  const trackWalkthroughAPI = async (endpoint) => {
    if (apiCallInProgress.current) {
      console.log('⏳ API call already in progress, skipping');
      return;
    }
    
    apiCallInProgress.current = true;
    
    try {
      const token = await user.getIdToken();
      
      // Use absolute URL in production, relative in development
      // In production, REACT_APP_BACKEND_URL should be set (e.g., https://4rked.com/api)
      // In development, use relative path which will use the proxy
      const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;
      const apiUrl = apiBaseUrl 
        ? `${apiBaseUrl}/users/${endpoint}`
        : `/api/users/${endpoint}`;
      
      console.log(`📡 Calling walkthrough API: ${apiUrl}`);
      console.log(`🔑 Using token: ${token ? token.substring(0, 20) + '...' : 'NO TOKEN'}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // Prevent caching
        cache: 'no-cache'
      });
      
      const responseData = await response.json().catch((e) => {
        console.error('Failed to parse response JSON:', e);
        return { error: 'Failed to parse response' };
      });
      
      console.log(`📥 Response status: ${response.status}`, responseData);
      
      if (!response.ok) {
        console.error(`❌ API call failed: ${response.status}`, responseData);
        throw new Error(`API call failed: ${response.status} - ${responseData.error || 'Unknown error'}`);
      }
      
      // Verify the response indicates success
      if (responseData.success !== false && response.status === 200) {
        console.log(`✅ ${endpoint} tracked successfully`, responseData);
      } else {
        console.warn(`⚠️ ${endpoint} returned unexpected response:`, responseData);
      }
    } catch (error) {
      console.error(`❌ Failed to track ${endpoint}:`, error);
      console.error(`❌ Error details:`, {
        message: error.message,
        stack: error.stack
      });
      // Don't throw - allow walkthrough to continue even if tracking fails
    } finally {
      apiCallInProgress.current = false;
    }
  };

  const startWalkthrough = async () => {
    if (isMobile) {
      return;
    }
    setShowWalkthrough(true);
    await trackWalkthroughAPI('walkthrough-start');
  };

  const completeWalkthrough = async () => {
    // Set completion flag immediately to prevent re-triggering
    justCompleted.current = true;
    completionTimestamp.current = Date.now();
    
    setShowWalkthrough(false);
    setIsNewUser(false);
    setWalkthroughStarted(false);
    localStorage.setItem('hasSeenWalkthrough', 'true');
    
    // Always track completion when this function is called
    // Don't check hasTrackedCompletion - we want to ensure it's marked as completed
    // even if called multiple times (e.g., from different handlers)
    hasTrackedCompletion.current = true;
    
    // Track completion API call - always call to ensure database is updated
    await trackWalkthroughAPI('walkthrough-complete');
    
    // Refresh user profile to get updated walkthrough_status
    if (user && refreshUserProfile) {
      // Small delay to ensure database update is complete
      setTimeout(async () => {
        try {
          await refreshUserProfile();
          // Keep the flag set for a bit longer to ensure profile refresh completes
          setTimeout(() => {
            justCompleted.current = false;
          }, 2000);
        } catch (err) {
          console.error('Error refreshing profile:', err);
          // Reset flag even on error after delay
          setTimeout(() => {
            justCompleted.current = false;
          }, 2000);
        }
      }, 500);
    } else {
      // If no refresh function, just reset after delay
      setTimeout(() => {
        justCompleted.current = false;
      }, 5000);
    }
  };

  const closeWalkthrough = async () => {
    // Set completion flag immediately to prevent re-triggering
    justCompleted.current = true;
    completionTimestamp.current = Date.now();
    
    setShowWalkthrough(false);
    setIsNewUser(false);
    setWalkthroughStarted(false);
    localStorage.setItem('hasSeenWalkthrough', 'true');
    
    // Only track completion once
    if (!hasTrackedCompletion.current) {
      hasTrackedCompletion.current = true;
      await trackWalkthroughAPI('walkthrough-complete');
      
      // Refresh user profile to get updated walkthrough_status
      if (user && refreshUserProfile) {
        setTimeout(async () => {
          try {
            await refreshUserProfile();
            // Keep the flag set for a bit longer to ensure profile refresh completes
            setTimeout(() => {
              justCompleted.current = false;
            }, 2000);
          } catch (err) {
            console.error('Error refreshing profile:', err);
            // Reset flag even on error after delay
            setTimeout(() => {
              justCompleted.current = false;
            }, 2000);
          }
        }, 500);
      } else {
        // If no refresh function, just reset after delay
        setTimeout(() => {
          justCompleted.current = false;
        }, 5000);
      }
    }
  };

  const stopWalkthrough = async () => {
    // Set completion flag immediately to prevent re-triggering
    justCompleted.current = true;
    completionTimestamp.current = Date.now();
    
    setShowWalkthrough(false);
    setIsNewUser(false);
    setWalkthroughStarted(false);
    localStorage.setItem('hasSeenWalkthrough', 'true');
    
    // Only track stop once
    if (!hasTrackedCompletion.current) {
      hasTrackedCompletion.current = true;
      await trackWalkthroughAPI('walkthrough-stop');
      
      // Refresh user profile to get updated walkthrough_status
      if (user && refreshUserProfile) {
        setTimeout(async () => {
          try {
            await refreshUserProfile();
            // Keep the flag set for a bit longer to ensure profile refresh completes
            setTimeout(() => {
              justCompleted.current = false;
            }, 2000);
          } catch (err) {
            console.error('Error refreshing profile:', err);
            // Reset flag even on error after delay
            setTimeout(() => {
              justCompleted.current = false;
            }, 2000);
          }
        }, 500);
      } else {
        // If no refresh function, just reset after delay
        setTimeout(() => {
          justCompleted.current = false;
        }, 5000);
      }
    }
  };

  const resetWalkthrough = () => {
    localStorage.removeItem('hasSeenWalkthrough');
    localStorage.removeItem('userSessionStarted');
    setShowWalkthrough(false);
    setIsNewUser(false);
    setWalkthroughStarted(false);
    hasTrackedCompletion.current = false;
    justCompleted.current = false;
    completionTimestamp.current = null;
  };

  return (
    <WalkthroughContext.Provider
      value={{
        showWalkthrough,
        isNewUser,
        isMobile,
        startWalkthrough,
        completeWalkthrough,
        closeWalkthrough,
        stopWalkthrough,
        resetWalkthrough
      }}
    >
      {children}
    </WalkthroughContext.Provider>
  );
};

export const useWalkthrough = () => {
  const context = useContext(WalkthroughContext);
  if (!context) {
    throw new Error('useWalkthrough must be used within a WalkthroughProvider');
  }
  return context;
};

// Also export as default for testing
export default WalkthroughProvider;